import { ChartLegendItem } from "src/components/monitoring/commonTypes";
import { SenecBaseTheme } from "src/styles/senecBaseTheme";
import { Box, Skeleton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { formatLegendValue, NO_VALUE_INDICATOR } from "src/components/monitoring/chartUtils";
import { formatDataSeriesUnitToString } from "src/components/monitoring/senec-chart-legend/SenecChartLegendUtils";

export const LegendItem = ({
    chartLegendItem,
    selectedLegendTypes,
    isLoading,
    locale,
    showNoValueIndicator,
    isBigValue,
    linePosition = "left",
}: {
    chartLegendItem: ChartLegendItem;
    selectedLegendTypes: string[];
    isLoading?: boolean;
    locale: Intl.Locale;
    showNoValueIndicator: boolean;
    isBigValue: boolean;
    /**
     * Position of the decorative line
     * @default "left"
     */
    linePosition: "top" | "left";
}) => {
    const theme: SenecBaseTheme = useTheme();
    const desktopView = useMediaQuery(theme.breakpoints.up("tablet"));
    const legendValue = formatLegendValue(
        locale.toString(),
        chartLegendItem.legendValue,
        showNoValueIndicator,
        true,
        chartLegendItem.dataSeriesUnit,
    );
    const unit =
        chartLegendItem.dataSeriesUnit !== undefined ? formatDataSeriesUnitToString(chartLegendItem.dataSeriesUnit) : undefined;
    const fontColor = selectedLegendTypes.includes(chartLegendItem.id)
        ? theme.palette.neutralGray110.main
        : theme.palette.neutralGray50.main;
    return (
        <Stack height="100%" width={{ xsMobile: "100%" }} minWidth="72px" direction={linePosition === "top" ? "column" : "row"}>
            <Box
                data-testid={"legend-item-line-" + chartLegendItem.id}
                sx={{
                    backgroundColor: selectedLegendTypes.includes(chartLegendItem.id)
                        ? chartLegendItem.color
                        : theme.palette.neutralGray10.main,
                    height: linePosition === "left" ? "100%" : "2px",
                    width: linePosition === "left" ? "2px" : "100%",
                    minWidth: linePosition === "left" ? "2px" : "100%",
                    borderRadius: "2px",
                }}
            />
            <Stack gap={0.5} padding={1} width="100%">
                <Typography
                    variant={"subtitle1"}
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {chartLegendItem.translation}
                </Typography>

                <Stack direction={"row"} justifyContent={"flex-start"} alignItems={"baseline"}>
                    {isLoading ? (
                        <Skeleton width={"100%"} height="30px" data-testid="skeleton" />
                    ) : (
                        legendValue !== "" && (
                            <>
                                <Typography
                                    data-testid={"legend-item-text-" + chartLegendItem.id}
                                    className="legend-item-value"
                                    variant="h4"
                                    color={fontColor}
                                    sx={{
                                        fontSize: isBigValue ? "12px !important" : "auto !important",
                                        fontWeight: 500,
                                        lineHeight: "150%",
                                    }}
                                >
                                    {chartLegendItem.skipLegendValueProcessing
                                        ? chartLegendItem.legendValue
                                        : desktopView && legendValue !== NO_VALUE_INDICATOR && unit
                                          ? legendValue + unit
                                          : legendValue}
                                </Typography>

                                {unit && (
                                    <Typography
                                        className="legend-item-unit-mobile"
                                        variant={"subtitle1"}
                                        sx={{ fontFamily: "Mark Pro, sans-serif", fontWeight: 500, lineHeight: "150%" }}
                                        display={desktopView ? "none" : "flex"}
                                        color={fontColor}
                                    >
                                        {unit}
                                    </Typography>
                                )}
                            </>
                        )
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
};
